/**
 * Space announcements: limits, wire types, and audience rules.
 *
 * An announcement is a Space-level post (not tied to a channel) shown on the
 * Space Home Announcements tab. Its body is a composer payload (plaintext, or
 * Community Cipher ciphertext on E2EE Spaces) with an optional title inside.
 *
 * Audience: empty `channelIds` and `roleIds` means every member. Within each
 * list a viewer needs ANY entry; between the lists `match` picks AND (`all`,
 * the default) or OR (`any`). An empty list places no limit, so `match` only
 * matters when both lists are non-empty.
 *
 * @module api/space-announcements
 */
/** Max length of the optional title (inside the payload, so client-enforced on E2EE). */
export const SPACE_ANNOUNCEMENT_TITLE_MAX_LENGTH = 120;
/** Max visible body text (excluding media tokens), matches channel messages. */
export const SPACE_ANNOUNCEMENT_TEXT_MAX_LENGTH = 4000;
/** Max serialized plaintext payload (text + title + inline media metadata). */
export const SPACE_ANNOUNCEMENT_CONTENT_MAX_LENGTH = 16384;
/** Max base64 ciphertext for E2EE announcements. */
export const SPACE_ANNOUNCEMENT_CIPHERTEXT_MAX_LENGTH = 32768;
/** Max uploaded attachments (images) per announcement. */
export const SPACE_ANNOUNCEMENT_MAX_ATTACHMENTS = 10;
/** Max channels / roles an announcement can be limited to. */
export const SPACE_ANNOUNCEMENT_AUDIENCE_MAX_CHANNELS = 25;
export const SPACE_ANNOUNCEMENT_AUDIENCE_MAX_ROLES = 25;
/** Between-list match modes: `all` = channel AND role, `any` = channel OR role. */
export const SPACE_ANNOUNCEMENT_AUDIENCE_MATCHES = ['all', 'any'];
/** Default and max page size for the Announcements tab. */
export const SPACE_ANNOUNCEMENT_PAGE_DEFAULT = 20;
export const SPACE_ANNOUNCEMENT_PAGE_MAX = 50;
/** Toasts only consider announcements newer than this window. */
export const SPACE_ANNOUNCEMENT_TOAST_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
/** Max toasts shown at once (latest unseen, displayed oldest first). */
export const SPACE_ANNOUNCEMENT_TOAST_LIMIT = 5;
/** Title / body excerpt lengths for toasts. */
export const SPACE_ANNOUNCEMENT_TOAST_TITLE_CHARS = 80;
export const SPACE_ANNOUNCEMENT_TOAST_BODY_CHARS = 140;
/** Dedupes and sorts ids, and resets `match` when it cannot matter. */
export function normalizeAnnouncementAudience(audience) {
    const channelIds = [...new Set(audience?.channelIds ?? [])].sort();
    const roleIds = [...new Set(audience?.roleIds ?? [])].sort();
    const match = channelIds.length > 0 && roleIds.length > 0 && audience?.match === 'any' ? 'any' : 'all';
    return { channelIds, roleIds, match };
}
/** True when the announcement is not limited by channel or role. */
export function isUnrestrictedAnnouncementAudience(audience) {
    return audience.channelIds.length === 0 && audience.roleIds.length === 0;
}
/** True when a viewer qualifies for the audience. */
export function matchesAnnouncementAudience(audience, viewer) {
    const hasChannels = audience.channelIds.length > 0;
    const hasRoles = audience.roleIds.length > 0;
    if (!hasChannels && !hasRoles)
        return true;
    const channelOk = audience.channelIds.some((id) => viewer.viewableChannelIds.has(id));
    const roleOk = audience.roleIds.some((id) => viewer.roleIds.has(id));
    if (!hasChannels)
        return roleOk;
    if (!hasRoles)
        return channelOk;
    return audience.match === 'any' ? channelOk || roleOk : channelOk && roleOk;
}
/**
 * Where a qualifying viewer sees the toast: the targeted channels, or `null`
 * for any channel (no channel limit, or an OR match met only by role).
 */
export function resolveAnnouncementToastChannelIds(audience, viewer) {
    if (audience.channelIds.length === 0)
        return null;
    if (audience.match === 'any' && audience.roleIds.length > 0) {
        const viaChannel = audience.channelIds.some((id) => viewer.viewableChannelIds.has(id));
        if (!viaChannel)
            return null;
    }
    return [...audience.channelIds];
}
//# sourceMappingURL=space-announcements.js.map